import { useState, useEffect, useMemo, useRef } from "react";
import { FaArrowLeft, FaArrowRight, FaSearchPlus, FaSearchMinus, FaRedo } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import { getImageUrl } from "../../../../../Utils/localStorageUtils";

const ImageSlider = () => {
  const { propertyData } = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const imgRef = useRef(null);

  // ✅ Extract imageId from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const selectedImageId = queryParams.get("imageId");

  // ✅ Extract and sort images by category
  const images = useMemo(() => {
    if (!propertyData?.mediaFiles) return [];

    // Group images by category
    const categoryGroups = propertyData.mediaFiles.reduce((groups, file) => {
      const category = file.category || "Uncategorized";
      if (!groups[category]) groups[category] = [];
      groups[category].push({
        id: file._id,
        url: getImageUrl(file.fileName),
        category,
      });
      return groups;
    }, {});

    return Object.values(categoryGroups).flat();
  }, [propertyData]);

  // ✅ Find index of the selected imageId
  const initialIndex = useMemo(() => {
    const index = images.findIndex((file) => file.id === selectedImageId);
    return index !== -1 ? index : 0;
  }, [selectedImageId, images]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [direction, setDirection] = useState(1);
  const [imageError, setImageError] = useState(false);

  const [scale, setScale] = useState(1); // Zoom scale
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  // ✅ Function to update URL with the new imageId
  const updateURL = (newIndex) => {
    const newImageId = images[newIndex]?.id;
    if (newImageId) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("imageId", newImageId);
      navigate(newUrl.pathname + newUrl.search, { replace: true });
    }
  };

  const nextSlide = () => {
    setDirection(1);
    const newIndex = (currentIndex + 1) % images.length;
    setCurrentIndex(newIndex);
    setImageError(false);
    updateURL(newIndex);
    resetZoom();
  };

  const prevSlide = () => {
    setDirection(-1);
    const newIndex = (currentIndex - 1 + images.length) % images.length;
    setCurrentIndex(newIndex);
    setImageError(false);
    updateURL(newIndex);
    resetZoom();
  };

  // ✅ Keyboard navigation for left/right arrow keys
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") nextSlide();
      if (event.key === "ArrowLeft") prevSlide();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, images]);

  // ✅ Reset zoom to default
  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // ✅ Handle zoom (mouse wheel)
  const handleWheel = (event) => {
    event.preventDefault();
    setScale((prevScale) => Math.min(Math.max(prevScale + event.deltaY * -0.001, 1), 5));
  };

  // ✅ Start dragging
  const handleMouseDown = (event) => {
    if (scale > 1) {
      setDragging(true);
      setStartPos({ x: event.clientX - position.x, y: event.clientY - position.y });
    }
  };

  // ✅ Drag image
  const handleMouseMove = (event) => {
    if (!dragging) return;
    setPosition({ x: event.clientX - startPos.x, y: event.clientY - startPos.y });
  };

  // ✅ Stop dragging
  const handleMouseUp = () => setDragging(false);

  return (
    <div 
      className="relative w-full bg-black text-white p-4 select-none"
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseUp}
    >
      <div className="relative flex justify-center items-center overflow-hidden w-full">
        
        {/* Zoom Controls at Top-Right */}
        <div className="absolute top-4 right-4 bg-gray-900 p-2 rounded-lg flex gap-2 z-10">
          <button onClick={() => setScale((prev) => Math.min(prev + 0.2, 5))} className="text-white hover:text-gray-300">
            <FaSearchPlus size={20} />
          </button>
          <button onClick={() => setScale((prev) => Math.max(prev - 0.2, 1))} className="text-white hover:text-gray-300">
            <FaSearchMinus size={20} />
          </button>
          <button onClick={resetZoom} className="text-white hover:text-gray-300">
            <FaRedo size={20} />
          </button>
        </div>

        <button 
          onClick={prevSlide} 
          className="absolute left-4 p-2 bg-gray-800 rounded-full text-white z-10"
        >
          <FaArrowLeft size={20} />
        </button>

        {/* Image Transition Wrapper */}
        <div className="w-full h-[500px]  relative max-w-[80%] overflow-hidden">
          <AnimatePresence custom={direction} initial={false}>
            <motion.div
              key={currentIndex}
              className="absolute top-0 left-0 w-full h-full flex justify-center items-center"
              initial={{ x: direction * 100 + "%", opacity: 0 }}
              animate={{ x: "0%", opacity: 1 }}
              exit={{ x: -direction * 100 + "%", opacity: 0 }}
              transition={{ duration: 1, ease: "easeInOut" }}
            >
              {imageError || !images[currentIndex]?.url ? (
                <div className="flex justify-center items-center w-full h-full">
                  <FaRedo className="text-red-500 text-6xl" />
                </div>
              ) : (
                <img
                  ref={imgRef}
                  src={images[currentIndex].url}
                  alt={`Slide ${currentIndex + 1}`}
                  className="rounded-lg "
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    cursor: scale > 1 ? "grab" : "default",
                  }}
                  onError={() => setImageError(true)}
                  onWheel={handleWheel}
                  onMouseDown={handleMouseDown}
                  onDoubleClick={resetZoom}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <button 
          onClick={nextSlide} 
          className="absolute right-4 p-2 bg-gray-800 rounded-full text-white z-10"
        >
          <FaArrowRight size={20} />
        </button>
      </div>

      <div className="text-center mt-2 text-sm">
        {images.length > 0 ? `${currentIndex + 1} / ${images.length}` : "No images available"}
      </div>
    </div>
  );
};

export default ImageSlider;
